import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Home,
  ChevronRight,
  Download,
  Eye,
  Search,
  FileText,
  CalendarDays,
  ShieldCheck,
  X,
  RefreshCcw,
} from "lucide-react";

import jsPDF from "jspdf";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import { ASSETS } from "../../control";
import {
  financialCategories,
  financialRecords,
} from "../../data/financialRecords";

gsap.registerPlugin(ScrollTrigger);

const PublicFinancialRecords = () => {
  const pageRef = useRef(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [category, setCategory] = useState("All");
  const [year, setYear] = useState("All");
  const [period, setPeriod] = useState("All");
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [pdfUrl, setPdfUrl] = useState("");

  const latestRecord = financialRecords[0];

  const years = [...new Set(financialRecords.map((item) => item.financialYear))];
  const periods = [...new Set(financialRecords.map((item) => item.reportingPeriod))];

  const filteredRecords = useMemo(() => {
    return financialRecords.filter((record) => {
      const matchesSearch =
        record.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.description.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesCategory = category === "All" || record.category === category;
      const matchesYear = year === "All" || record.financialYear === year;
      const matchesPeriod = period === "All" || record.reportingPeriod === period;

      return matchesSearch && matchesCategory && matchesYear && matchesPeriod;
    });
  }, [searchTerm, category, year, period]);

  const resetFilters = () => {
    setSearchTerm("");
    setCategory("All");
    setYear("All");
    setPeriod("All");
  };

  const generatePDF = (record) => {
    const doc = new jsPDF("p", "mm", "a4");

    doc.setFillColor(2, 15, 46);
    doc.rect(0, 0, 210, 35, "F");

    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(15);
    doc.text("OIL AND GAS FREE ZONES AUTHORITY", 15, 15);

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text("Public Financial Records", 15, 23);

    doc.setTextColor(2, 15, 46);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.text(record.title, 15, 52, { maxWidth: 180 });

    doc.setDrawColor(242, 175, 17);
    doc.setLineWidth(1);
    doc.line(15, 60, 195, 60);

    doc.setFontSize(11);
    doc.setTextColor(35, 35, 35);

    const rows = [
      ["Category", record.category],
      ["Reporting Period", record.reportingPeriod],
      ["Financial Year", record.financialYear],
      ["Date Published", record.datePublished],
      ["File Format", record.fileFormat],
      ["Version", record.version || "1.0"],
      ["Status", record.status || "Published"],
      ["Approving Department", record.approvingDepartment || "Finance and Accounts Department"],
    ];

    let y = 75;

    rows.forEach(([label, value]) => {
      doc.setFont("helvetica", "bold");
      doc.text(`${label}:`, 15, y);
      doc.setFont("helvetica", "normal");
      doc.text(String(value), 65, y, { maxWidth: 125 });
      y += 10;
    });

    y += 8;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.text("Description", 15, y);

    y += 8;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.text(record.description, 15, y, { maxWidth: 180 });

    y += 35;

    doc.setFillColor(245, 247, 250);
    doc.roundedRect(15, y, 180, 35, 3, 3, "F");

    doc.setTextColor(2, 15, 46);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text("Public Notice", 22, y + 10);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    doc.text(
      "This document is generated from approved public financial record data for public information, transparency, accountability, and stakeholder reference.",
      22,
      y + 18,
      { maxWidth: 165 }
    );

    doc.setTextColor(100, 100, 100);
    doc.setFontSize(9);
    doc.text("Generated by OGFZA Public Financial Records System", 15, 285);

    return doc;
  };

  const openPreview = (record) => {
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
    }

    const doc = generatePDF(record);
    const blob = doc.output("blob");
    const url = URL.createObjectURL(blob);

    setSelectedRecord(record);
    setPdfUrl(url);
  };

  const closePreview = () => {
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
    }

    setSelectedRecord(null);
    setPdfUrl("");
  };

  const downloadRecord = (record) => {
    const doc = generatePDF(record);
    doc.save(record.fileName || `${record.title.replaceAll(" ", "-")}.pdf`);
  };

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(".hero-badge", {
        opacity: 0,
        y: 30,
        duration: 0.7,
        ease: "power3.out",
      });

      gsap.from(".hero-title", {
        opacity: 0,
        y: 80,
        duration: 1.1,
        ease: "power4.out",
      });

      gsap.from(".hero-text", {
        opacity: 0,
        y: 40,
        duration: 0.9,
        delay: 0.2,
        ease: "power3.out",
      });

      gsap.utils.toArray(".reveal-up").forEach((el) => {
        gsap.from(el, {
          opacity: 0,
          y: 70,
          duration: 0.85,
          ease: "power3.out",
          scrollTrigger: {
            trigger: el,
            start: "top 85%",
          },
        });
      });

      gsap.utils.toArray(".card-animate").forEach((el) => {
        gsap.from(el, {
          opacity: 0,
          y: 45,
          duration: 0.7,
          ease: "power3.out",
          scrollTrigger: {
            trigger: el,
            start: "top 90%",
          },
        });
      });

      gsap.to(".floating-orb-1", {
        y: 40,
        x: -20,
        duration: 6,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });

      gsap.to(".floating-orb-2", {
        y: -40,
        x: 20,
        duration: 7,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });
    }, pageRef);

    return () => ctx.revert();
  }, []);

  return (
    <main ref={pageRef} className="bg-white overflow-hidden">
      <section className="relative min-h-screen text-white flex items-center overflow-hidden">
        <img
          src={ASSETS.cover}
          alt="OGFZA Public Financial Records"
          className="absolute inset-0 w-full h-full object-cover"
        />

        <div className="absolute inset-0 bg-[#020f2e]/85" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#020f2e]/95 via-[#053b14]/75 to-[#F2AF11]/35" />

        <div className="floating-orb-1 absolute top-0 left-0 w-[500px] h-[500px] rounded-full bg-[#053b14]/40 blur-3xl" />
        <div className="floating-orb-2 absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full bg-[#F2AF11]/25 blur-3xl" />

        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.05)_1px,transparent_1px)] bg-[size:80px_80px]" />

        <div className="relative max-w-7xl mx-auto px-6 py-32">
          <div className="hero-badge inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white/10 border border-white/15 backdrop-blur-md mb-8">
            <span className="w-2 h-2 rounded-full bg-[#F2AF11]" />
            Public Information
          </div>

          <h1
            className="hero-title text-5xl md:text-8xl font-black leading-[0.95] max-w-6xl"
            style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
          >
            OGFZA Public Financial Records
          </h1>

          <p className="hero-text mt-8 max-w-3xl text-white/85 text-lg leading-[1.9]">
            Public access to approved financial records and disclosures of the
            Oil and Gas Free Zones Authority.
          </p>

          <div className="hero-text flex flex-wrap items-center gap-4 mt-10">
            <a
              href="#latest-record"
              className="inline-flex items-center gap-3 px-7 py-4 rounded-full bg-[#F2AF11] text-[#020f2e] font-black hover:bg-white transition-all duration-300"
            >
              View Latest Record
              <Eye size={18} />
            </a>

            <a
              href="#records-search"
              className="inline-flex items-center gap-3 px-7 py-4 rounded-full bg-white/10 border border-white/20 backdrop-blur-md text-white font-black hover:bg-white hover:text-[#020f2e] transition-all duration-300"
            >
              Search Records
              <Search size={18} />
            </a>
          </div>

          <div className="hero-text flex items-center gap-2 mt-8 text-sm text-white/75">
            <Link
              to="/"
              className="flex items-center gap-1 hover:text-[#F2AF11] transition-colors"
            >
              <Home size={14} />
              Home
            </Link>

            <ChevronRight size={14} />

            <span className="text-[#F2AF11]">Public Financial Records</span>
          </div>
        </div>
      </section>

      <section className="py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-[0.9fr_1.1fr] gap-16 items-center">
          <div className="reveal-up">
            <p className="text-[#053b14] font-black tracking-[0.2em] uppercase text-xs mb-4">
              Transparency & Accountability
            </p>

            <h2
              className="text-5xl md:text-6xl font-black leading-tight text-[#020f2e] mb-8"
              style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
            >
              Approved Financial Records For{" "}
              <span className="text-[#F2AF11]">Public Access</span>
            </h2>

            <div className="space-y-6 text-slate-700 leading-[2] text-[16.5px]">
              <p>
                The Oil and Gas Free Zones Authority publishes approved financial
                records and related financial disclosures to promote transparency,
                accountability, and public access to institutional information.
              </p>

              <p>
                Documents on this page are made available for public viewing and
                download after internal review and approval.
              </p>
            </div>
          </div>

          <div className="reveal-up grid sm:grid-cols-2 gap-5">
            {["Transparency", "Accountability", "Public Access", "Stakeholder Reference"].map(
              (item) => (
                <div
                  key={item}
                  className="rounded-[28px] p-7 bg-slate-50 border border-slate-100 hover:shadow-xl transition-all duration-300"
                >
                  <div className="w-14 h-14 rounded-2xl bg-[#053b14] text-white flex items-center justify-center mb-5">
                    <ShieldCheck size={24} />
                  </div>

                  <h3
                    className="text-2xl font-black text-[#020f2e]"
                    style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
                  >
                    {item}
                  </h3>
                </div>
              )
            )}
          </div>
        </div>
      </section>

      <section id="latest-record" className="py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="reveal-up max-w-3xl mb-12">
            <p className="text-[#053b14] font-black tracking-[0.2em] uppercase text-xs mb-4">
              Latest Published Record
            </p>

            <h2
              className="text-5xl md:text-6xl font-black text-[#020f2e] leading-tight"
              style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
            >
              Most Recent Financial Disclosure
            </h2>
          </div>

          <div className="reveal-up rounded-[40px] overflow-hidden bg-white shadow-[0_40px_120px_-30px_rgba(2,15,46,0.25)] border border-slate-100">
            <div className="grid lg:grid-cols-[0.8fr_1.2fr]">
              <div className="bg-gradient-to-br from-[#020f2e] via-[#053b14] to-[#F2AF11] p-10 text-white flex flex-col justify-between min-h-[420px]">
                <div>
                  <div className="w-20 h-20 rounded-[26px] bg-white/15 backdrop-blur-md flex items-center justify-center mb-8">
                    <FileText size={36} />
                  </div>

                  <p className="text-[#F2AF11] font-black tracking-[0.2em] uppercase text-xs mb-4">
                    Latest Record
                  </p>

                  <h3
                    className="text-4xl md:text-5xl font-black leading-tight"
                    style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
                  >
                    {latestRecord.title}
                  </h3>
                </div>

                <div className="flex items-center gap-2 text-white/80 mt-10">
                  <CalendarDays size={18} />
                  Published: {latestRecord.datePublished}
                </div>
              </div>

              <div className="p-8 md:p-12">
                <div className="grid sm:grid-cols-2 gap-5 mb-8">
                  <InfoBox label="Category" value={latestRecord.category} />
                  <InfoBox label="Financial Year" value={latestRecord.financialYear} />
                  <InfoBox label="Reporting Period" value={latestRecord.reportingPeriod} />
                  <InfoBox label="File Format" value={latestRecord.fileFormat} />
                </div>

                <p className="text-slate-700 leading-[2] text-[16.5px] mb-8">
                  {latestRecord.description}
                </p>

                <div className="flex flex-wrap gap-4">
                  <button
                    onClick={() => openPreview(latestRecord)}
                    className="inline-flex items-center gap-3 px-7 py-4 rounded-full bg-[#053b14] text-white font-black hover:bg-[#020f2e] transition-all duration-300"
                  >
                    Preview PDF
                    <Eye size={18} />
                  </button>

                  <button
                    onClick={() => downloadRecord(latestRecord)}
                    className="inline-flex items-center gap-3 px-7 py-4 rounded-full bg-[#F2AF11] text-[#020f2e] font-black hover:bg-[#020f2e] hover:text-white transition-all duration-300"
                  >
                    Download PDF
                    <Download size={18} />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="reveal-up mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {financialRecords.map((record) => (
              <RecordCard
                key={record.id}
                record={record}
                onPreview={openPreview}
                onDownload={downloadRecord}
              />
            ))}
          </div>
        </div>
      </section>

      <section id="records-search" className="py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="reveal-up max-w-3xl mb-12">
            <p className="text-[#053b14] font-black tracking-[0.2em] uppercase text-xs mb-4">
              Search & Filter
            </p>

            <h2
              className="text-5xl md:text-6xl font-black text-[#020f2e] leading-tight"
              style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
            >
              Public Records Archive
            </h2>
          </div>

          <div className="reveal-up rounded-[35px] bg-slate-50 border border-slate-100 p-6 md:p-8 mb-10">
            <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
              <div className="lg:col-span-2 relative">
                <Search
                  size={18}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                />

                <input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by title or description"
                  className="w-full h-14 rounded-2xl bg-white border border-slate-200 pl-12 pr-4 font-semibold outline-none focus:border-[#053b14]"
                />
              </div>

              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="h-14 rounded-2xl bg-white border border-slate-200 px-4 font-semibold outline-none focus:border-[#053b14]"
              >
                <option value="All">All Categories</option>
                {financialCategories.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>

              <select
                value={year}
                onChange={(e) => setYear(e.target.value)}
                className="h-14 rounded-2xl bg-white border border-slate-200 px-4 font-semibold outline-none focus:border-[#053b14]"
              >
                <option value="All">All Years</option>
                {years.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>

              <button
                onClick={resetFilters}
                className="h-14 rounded-2xl bg-[#020f2e] text-white font-black flex items-center justify-center gap-2 hover:bg-[#053b14] transition-all duration-300"
              >
                <RefreshCcw size={18} />
                Reset
              </button>
            </div>

            <div className="mt-4">
              <select
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                className="w-full h-14 rounded-2xl bg-white border border-slate-200 px-4 font-semibold outline-none focus:border-[#053b14]"
              >
                <option value="All">All Reporting Periods</option>
                {periods.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="hidden lg:block reveal-up rounded-[30px] overflow-hidden border border-slate-100 shadow-xl">
            <table className="w-full">
              <thead className="bg-[#020f2e] text-white">
                <tr>
                  <th className="p-5 text-left text-sm">S/N</th>
                  <th className="p-5 text-left text-sm">Document Title</th>
                  <th className="p-5 text-left text-sm">Category</th>
                  <th className="p-5 text-left text-sm">Period</th>
                  <th className="p-5 text-left text-sm">Year</th>
                  <th className="p-5 text-left text-sm">Published</th>
                  <th className="p-5 text-left text-sm">Action</th>
                </tr>
              </thead>

              <tbody>
                {filteredRecords.map((record, index) => (
                  <tr key={record.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="p-5 font-bold">{index + 1}</td>
                    <td className="p-5 font-black text-[#020f2e]">{record.title}</td>
                    <td className="p-5 text-slate-600">{record.category}</td>
                    <td className="p-5 text-slate-600">{record.reportingPeriod}</td>
                    <td className="p-5 font-bold">{record.financialYear}</td>
                    <td className="p-5 text-slate-600">{record.datePublished}</td>
                    <td className="p-5">
                      <div className="flex gap-2">
                        <button
                          onClick={() => openPreview(record)}
                          className="w-10 h-10 rounded-xl bg-[#053b14] text-white flex items-center justify-center hover:bg-[#020f2e]"
                          title="Preview PDF"
                        >
                          <Eye size={17} />
                        </button>

                        <button
                          onClick={() => downloadRecord(record)}
                          className="w-10 h-10 rounded-xl bg-[#F2AF11] text-[#020f2e] flex items-center justify-center hover:bg-[#020f2e] hover:text-white"
                          title="Download PDF"
                        >
                          <Download size={17} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="lg:hidden reveal-up grid gap-5">
            {filteredRecords.map((record) => (
              <RecordCard
                key={record.id}
                record={record}
                onPreview={openPreview}
                onDownload={downloadRecord}
              />
            ))}
          </div>
        </div>
      </section>

      {selectedRecord && (
        <div className="fixed inset-0 z-[10000] bg-black/80 flex items-center justify-center p-3 md:p-6">
          <div className="bg-white w-full max-w-6xl h-[92vh] rounded-[24px] overflow-hidden shadow-2xl flex flex-col">
            <div className="px-5 md:px-7 py-4 bg-[#020f2e] text-white flex items-center justify-between gap-4">
              <div>
                <p className="text-[#F2AF11] text-xs font-black uppercase tracking-[0.2em]">
                  PDF Preview
                </p>

                <h3 className="font-black text-lg md:text-xl leading-tight">
                  {selectedRecord.title}
                </h3>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => downloadRecord(selectedRecord)}
                  className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F2AF11] text-[#020f2e] font-black"
                >
                  Download
                  <Download size={16} />
                </button>

                <button
                  onClick={closePreview}
                  className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white hover:text-[#020f2e]"
                >
                  <X size={22} />
                </button>
              </div>
            </div>

            <div className="grid lg:grid-cols-[0.34fr_0.66fr] flex-1 min-h-0">
              <div className="p-5 md:p-7 bg-slate-50 border-r border-slate-200 overflow-y-auto">
                <InfoBox label="Category" value={selectedRecord.category} />
                <InfoBox label="Reporting Period" value={selectedRecord.reportingPeriod} />
                <InfoBox label="Financial Year" value={selectedRecord.financialYear} />
                <InfoBox label="Date Published" value={selectedRecord.datePublished} />
                <InfoBox label="File Format" value={selectedRecord.fileFormat} />

                <div className="mt-5 rounded-2xl bg-white border border-slate-100 p-5">
                  <p className="text-[11px] uppercase tracking-[0.2em] font-black text-[#053b14] mb-2">
                    Description
                  </p>

                  <p className="text-slate-700 leading-[1.8]">
                    {selectedRecord.description}
                  </p>
                </div>

                <button
                  onClick={() => downloadRecord(selectedRecord)}
                  className="sm:hidden mt-5 w-full h-12 rounded-full bg-[#F2AF11] text-[#020f2e] font-black flex items-center justify-center gap-2"
                >
                  Download PDF
                  <Download size={17} />
                </button>
              </div>

              <div className="bg-slate-200 min-h-0">
                {pdfUrl && (
                  <iframe
                    src={pdfUrl}
                    title={selectedRecord.title}
                    className="w-full h-full min-h-[500px]"
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

const InfoBox = ({ label, value }) => (
  <div className="rounded-2xl bg-white border border-slate-100 p-5 mb-4">
    <p className="text-[11px] uppercase tracking-[0.2em] font-black text-[#053b14] mb-2">
      {label}
    </p>

    <p className="font-black text-[#020f2e]">{value}</p>
  </div>
);

const RecordCard = ({ record, onPreview, onDownload }) => (
  <div className="card-animate rounded-[28px] bg-white border border-slate-100 p-6 shadow-[0_25px_70px_-35px_rgba(2,15,46,0.35)] hover:shadow-xl transition-all duration-300">
    <div className="w-14 h-14 rounded-2xl bg-[#053b14] text-white flex items-center justify-center mb-5">
      <FileText size={24} />
    </div>

    <p className="text-[#F2AF11] text-xs font-black uppercase tracking-[0.18em] mb-3">
      {record.category}
    </p>

    <h3
      className="text-2xl font-black text-[#020f2e] leading-tight mb-4"
      style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
    >
      {record.title}
    </h3>

    <div className="space-y-2 text-sm text-slate-600 mb-5">
      <p>
        <strong>Period:</strong> {record.reportingPeriod}
      </p>
      <p>
        <strong>Year:</strong> {record.financialYear}
      </p>
      <p>
        <strong>Published:</strong> {record.datePublished}
      </p>
      <p>
        <strong>Format:</strong> {record.fileFormat}
      </p>
    </div>

    <div className="flex flex-wrap gap-3">
      <button
        onClick={() => onPreview(record)}
        className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#053b14] text-white font-black hover:bg-[#020f2e] transition-all duration-300"
      >
        Preview
        <Eye size={16} />
      </button>

      <button
        onClick={() => onDownload(record)}
        className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#F2AF11] text-[#020f2e] font-black hover:bg-[#020f2e] hover:text-white transition-all duration-300"
      >
        Download
        <Download size={16} />
      </button>
    </div>
  </div>
);

export default PublicFinancialRecords;